import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPostStream, apiPut } from '../lib/api-client';
import { EmailTemplateSelect } from '../components/documents/TemplateSelects';
import { apiError } from '../lib/utils';
import { ProposalPreview } from '../components/documents/ProposalPreview';
import { QuotationFormat2 } from '../components/documents/QuotationFormat2';
import { QuotationPreview } from '../components/documents/QuotationPreview';
import { EmailFields } from '../components/documents/EmailFields';
import { EmailPreview } from '../components/documents/EmailPreview';
import { Alert } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useActivity } from '../components/ui/Activity';
import { useNotice } from '../components/ui/Notice';
import { PageHead, SectionCard } from '../components/ui/chrome';
import { defaultSender, type CompanyMailbox } from '../lib/company-mail';
import { ReviewChoiceDialog } from '../components/documents/ReviewChoiceDialog';
import { filesOf, ProposalFileLinks, uploadProposalFile, deleteProposalFile, uploadQuotationFile, deleteQuotationFile } from '../components/documents/ProposalFiles';
import { reviewTarget } from '../components/documents/review-target';

interface Doc {
  id: string;
  kind: string;
  number?: string;
  title?: string;
  status?: string;
  revision?: number;
  proposalId?: string;
  customerName?: string;
  summary?: string;
  understanding?: string;
  approach?: string;
  stages?: string;
  deliverables?: string;
  commercial?: string;
  assumptions?: string;
  whyMaxiron?: string;
  nextSteps?: string;
  purpose?: string;
  scope?: string;
  issuedOn?: string;
  preparedFor?: string;
  preparedBy?: string;
  layout?: string;
  currency?: string;
  validDays?: number;
  billToName?: string;
  billToAddress?: string;
  billToEmail?: string;
  contactName?: string;
  contactRole?: string;
  contactEmail?: string;
  contactPhone?: string;
  notice?: string;
  paymentTerms?: string;
  delivery?: string;
  acceptance?: string;
  lines?: { description: string; qty: number; unitPence: number }[];
  vatPence?: number | null;
  recipients?: string[];
  emailSubject?: string;
  emailBody?: string;
  sharePointUrl?: string;
  externalFiles?: { name: string; url: string; path: string }[];
}

type DocsChoice = 'proposal' | 'quotation' | 'both';

interface OfferPick {
  proposalPdf: boolean;
  quotationPdf: boolean;
  files: Record<string, boolean>;
}

function startingPick(proposal: Doc | null, quotation: Doc | null): OfferPick {
  const files: Record<string, boolean> = {};
  for (const file of [...filesOf(proposal), ...filesOf(quotation)]) files[file.path] = true;
  return { proposalPdf: Boolean(proposal), quotationPdf: Boolean(quotation), files };
}

export function ReviewIssuePage() {
  const notice = useNotice();
  const activity = useActivity();
  const { id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const docs = search.get('docs');
  const choice: DocsChoice | null = docs === 'proposal' || docs === 'quotation' || docs === 'both' ? docs : null;
  const [error, setError] = useState('');
  const [proposal, setProposal] = useState<Doc | null>(null);
  const [quotation, setQuotation] = useState<Doc | null>(null);
  const [pick, setPick] = useState<OfferPick>({ proposalPdf: false, quotationPdf: false, files: {} });
  const [pickReady, setPickReady] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [preview, setPreview] = useState<'proposal' | 'quotation'>('proposal');
  const [from, setFrom] = useState<CompanyMailbox>(defaultSender('offer'));
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  useEffect(() => {
    if (!id) return;
    void apiGet<Doc[]>('/api/commercial').then(async (rows) => {
      const target = reviewTarget(rows || [], id);
      if (!target) { setError('Offer not found'); return; }
      const proposalRow = target.proposalId ? await apiGet<Doc>(`/api/proposals/${target.proposalId}`).catch(() => null) : null;
      const quoteRow = target.quotationId ? await apiGet<Doc>(`/api/quotations/${target.quotationId}`).catch(() => null) : null;
      setProposal(proposalRow);
      setQuotation(quoteRow);
    }).catch((err) => setError(apiError(err, 'Could not open review')));
  }, [id]);

  useEffect(() => {
    if (!choice) return;
    const source = choice === 'proposal' ? proposal : quotation;
    if (!source) return;
    const recipients = source.recipients?.length ? source.recipients : (source.billToEmail ? [source.billToEmail] : []);
    setTo(recipients);
    setSubject(source.emailSubject || source.title || '');
    setBody(source.emailBody || '');
    setPreview(choice === 'proposal' ? 'proposal' : 'quotation');
  }, [choice, proposal, quotation]);

  useEffect(() => {
    if (!choice || pickReady || (!proposal && !quotation)) return;
    setPick(startingPick(proposal, quotation));
    setPickReady(true);
  }, [choice, proposal, quotation, pickReady]);

  const mailFromProposal = choice === 'proposal';
  const senderName = mailFromProposal ? proposal?.preparedBy : quotation?.contactName;
  const senderTitle = mailFromProposal ? proposal?.contactRole : quotation?.contactRole;
  const senderEmail = mailFromProposal ? proposal?.contactEmail : quotation?.contactEmail;
  const senderPhone = mailFromProposal ? proposal?.contactPhone : quotation?.contactPhone;

  async function addFile(file: File, kind: 'proposal' | 'quotation') {
    const target = kind === 'quotation' ? quotation : proposal;
    if (!target) return;
    setFileBusy(true);
    try {
      const saved = kind === 'quotation'
        ? await uploadQuotationFile(target.id, file)
        : await uploadProposalFile(target.id, file);
      const added = filesOf(saved).filter((item) => !filesOf(target).some((current) => current.path === item.path));
      if (kind === 'quotation') setQuotation({ ...target, ...saved } as Doc);
      else setProposal({ ...target, ...saved } as Doc);
      setPick((current) => {
        const files = { ...current.files };
        for (const item of added) files[item.path] = true;
        return { ...current, files };
      });
      notice.success('File attached');
    } catch (err) {
      notice.failure(apiError(err, 'Could not attach that file'));
    } finally {
      setFileBusy(false);
    }
  }

  async function removeFile(path: string, kind?: 'proposal' | 'quotation') {
    const which = kind ?? (choice === 'quotation' ? 'quotation' : 'proposal');
    const target = which === 'quotation' ? quotation : proposal;
    if (!target) return;
    setFileBusy(true);
    try {
      const saved = which === 'quotation'
        ? await deleteQuotationFile(target.id, path)
        : await deleteProposalFile(target.id, path);
      if (which === 'quotation') setQuotation({ ...target, ...saved } as Doc);
      else setProposal({ ...target, ...saved } as Doc);
    } catch (err) {
      notice.failure(apiError(err, 'Could not remove that file'));
    } finally {
      setFileBusy(false);
    }
  }

  function fileOn(path: string): boolean {
    return pick.files[path] !== false;
  }

  function selectedFiles() {
    return [...filesOf(proposal), ...filesOf(quotation)].filter((file) => fileOn(file.path));
  }

  function selectedNames(): string[] {
    return [
      pick.proposalPdf && proposal ? `Proposal ${proposal.number || ''}`.trim() : '',
      ...filesOf(proposal).filter((file) => fileOn(file.path)).map((file) => file.name),
      ...filesOf(quotation).filter((file) => fileOn(file.path)).map((file) => file.name),
      pick.quotationPdf && quotation ? `Quotation ${quotation.number || ''}`.trim() : '',
    ].filter(Boolean);
  }

  async function saveDraftMessage(kind: 'proposal' | 'quotation', row: Doc | null) {
    if (!row || row.status === 'issued' || row.status === 'accepted') return;
    const url = kind === 'quotation' ? `/api/quotations/${row.id}` : `/api/proposals/${row.id}`;
    await apiPut(url, { emailSubject: subject, emailBody: body });
  }

  async function issue(send: boolean) {
    const names = selectedNames();
    if (send && !names.length) {
      notice.failure('Choose an attachment');
      return;
    }
    if (!send && !pick.proposalPdf && !pick.quotationPdf) {
      notice.failure('Choose a document');
      return;
    }
    if (proposal && (pick.proposalPdf || filesOf(proposal).some((file) => fileOn(file.path)))) await saveDraftMessage('proposal', proposal);
    if (quotation && (pick.quotationPdf || filesOf(quotation).some((file) => fileOn(file.path)))) await saveDraftMessage('quotation', quotation);
    if (!send) {
      await notice.run('Issuing the offer', async () => {
        if (pick.proposalPdf && proposal) await apiPost(`/api/proposals/${proposal.id}/status`, { status: 'issued' });
        if (pick.quotationPdf && quotation) await apiPost(`/api/quotations/${quotation.id}/status`, { status: 'issued' });
      }, 'Marked issued. No email was sent.');
      return;
    }
    const number = proposal?.number || quotation?.number || 'Offer';
    const revision = String((pick.proposalPdf ? proposal?.revision : quotation?.revision) || proposal?.revision || quotation?.revision || 1);
    const attachments = names.join(', ');
    await activity.track({
      title: 'Sending your offer',
      subtitle: subject || proposal?.title || quotation?.title || number,
      dockTitle: `Sending offer ${number}`,
      to: to.join(', '),
      attachments,
      revision,
      kindLabel: 'Offer',
      steps: [
        { id: 'documents', label: 'Documents prepared', detail: `${attachments} · Revision ${revision}` },
        { id: 'issued', label: 'Revision issued', detail: `Revision ${revision}` },
        { id: 'email', label: 'Sending email', detail: 'Waiting for the mail service to confirm.' },
      ],
    }, (apply, signal) => apiPostStream('/api/sales-offers/issue', {
      proposalId: proposal?.id,
      quotationId: quotation?.id,
      recipients: to,
      cc,
      subject,
      body,
      contactName: senderName,
      contactRole: senderTitle,
      contactEmail: senderEmail,
      contactPhone: senderPhone,
      from,
      progress: true,
      attach: {
        proposalPdf: Boolean(proposal && pick.proposalPdf),
        quotationPdf: Boolean(quotation && pick.quotationPdf),
        files: selectedFiles().map((file) => file.path),
      },
    }, apply, {}, signal));
  }

  const lines = (quotation?.lines || []).map((line) => ({ description: line.description, qty: String(line.qty), pounds: ((line.unitPence || 0) / 100).toFixed(2) }));
  const allowProposal = Boolean(proposal);
  const allowQuotation = Boolean(quotation);
  const nothingSelected = !selectedNames().length;

  if (!choice) {
    return (
      <div>
        <PageHead
          crumbs={[{ label: 'Sales offers', to: '/quotations' }, { label: proposal?.number || quotation?.number || 'Offer', to: proposal ? `/proposals/${proposal.id}` : quotation ? `/quotations/${quotation.id}` : '/quotations' }, { label: 'Review & issue' }]}
          back={{ label: 'Back to offer', to: proposal ? `/proposals/${proposal.id}` : quotation ? `/quotations/${quotation.id}` : '/quotations' }}
          title="Review & issue"
          subtitle="Choose the proposal, the quotation, or both."
        />
        {error ? <Alert error>{error}</Alert> : null}
        <ReviewChoiceDialog
          reviewId={id || ''}
          proposalId={proposal?.id}
          quotationId={quotation?.id}
          onClose={() => navigate(proposal ? `/proposals/${proposal.id}` : quotation ? `/quotations/${quotation.id}` : '/quotations')}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHead
        crumbs={[{ label: 'Sales offers', to: '/quotations' }, { label: proposal?.number || quotation?.number || 'Offer', to: proposal ? `/proposals/${proposal.id}` : quotation ? `/quotations/${quotation.id}` : '/quotations' }, { label: 'Review & issue' }]}
        back={{ label: 'Back to offer', to: proposal ? `/proposals/${proposal.id}` : quotation ? `/quotations/${quotation.id}` : '/quotations' }}
        title="Review & issue"
        subtitle="Check the documents and message before sending."
        actions={
          <>
            <Button variant="outline" disabled={!pick.proposalPdf && !pick.quotationPdf} onClick={() => void issue(false)}>Issue without email</Button>
            <Button disabled={nothingSelected} onClick={() => void issue(true)}>Issue & send</Button>
          </>
        }
      />
      {error ? <Alert error>{error}</Alert> : null}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <SectionCard title="Documents to include" hint="Each document and each file is its own tick. Unticking one leaves the others as they are.">
            {proposal ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={pick.proposalPdf} onChange={() => setPick((current) => ({ ...current, proposalPdf: !current.proposalPdf }))} /> Proposal {proposal.number} {proposal.revision ? `Revision ${proposal.revision}` : ''}</label>
                {filesOf(proposal).map((file) => (
                  <div key={file.path} className="ml-6 flex items-center justify-between gap-2 text-sm">
                    <label className="flex min-w-0 items-center gap-2"><input type="checkbox" checked={fileOn(file.path)} onChange={() => setPick((current) => ({ ...current, files: { ...current.files, [file.path]: current.files[file.path] === false } }))} /><span className="truncate">{file.name}</span></label>
                    {proposal.status !== 'accepted' ? <button type="button" className="shrink-0 text-red-600" disabled={fileBusy} onClick={() => void removeFile(file.path, 'proposal')}>Remove</button> : null}
                  </div>
                ))}
                {proposal.status !== 'accepted' ? <div className="ml-6"><ProposalFileLinks files={[]} busy={fileBusy} onPick={(file) => void addFile(file, 'proposal')} /></div> : null}
              </div>
            ) : null}
            {quotation ? (
              <div className={`space-y-2 ${proposal ? 'mt-3' : ''}`}>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={pick.quotationPdf} onChange={() => setPick((current) => ({ ...current, quotationPdf: !current.quotationPdf }))} /> Quotation {quotation.number} {quotation.revision ? `Revision ${quotation.revision}` : ''}</label>
                {filesOf(quotation).map((file) => (
                  <div key={file.path} className="ml-6 flex items-center justify-between gap-2 text-sm">
                    <label className="flex min-w-0 items-center gap-2"><input type="checkbox" checked={fileOn(file.path)} onChange={() => setPick((current) => ({ ...current, files: { ...current.files, [file.path]: current.files[file.path] === false } }))} /><span className="truncate">{file.name}</span></label>
                    {quotation.status !== 'accepted' ? <button type="button" className="shrink-0 text-red-600" disabled={fileBusy} onClick={() => void removeFile(file.path, 'quotation')}>Remove</button> : null}
                  </div>
                ))}
                {quotation.status !== 'accepted' ? <div className="ml-6"><ProposalFileLinks files={[]} busy={fileBusy} onPick={(file) => void addFile(file, 'quotation')} /></div> : null}
              </div>
            ) : null}
          </SectionCard>
          <SectionCard title="Email" hint="The message sent with the selected documents.">
            <EmailTemplateSelect kind={preview === 'quotation' ? 'quotation' : 'proposal'} values={{ number: (preview === 'quotation' ? quotation?.number : proposal?.number) || '', customer: (preview === 'quotation' ? quotation?.customerName : proposal?.customerName) || '', title: (preview === 'quotation' ? quotation?.title : proposal?.title) || '' }} onApply={(nextSubject, nextBody) => { setSubject(nextSubject); setBody(nextBody); }} />
            <EmailFields from={from} to={to} cc={cc} subject={subject} body={body} onFrom={setFrom} onTo={setTo} onCc={setCc} onSubject={setSubject} onBody={setBody} />
          </SectionCard>
          <EmailPreview
            from={from}
            recipients={to.join(', ')}
            cc={cc.join(', ')}
            subject={subject}
            body={body}
            senderName={senderName}
            senderTitle={senderTitle}
            senderEmail={senderEmail}
            senderPhone={senderPhone}
          />
        </div>
        <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-preview)] p-3">
          <div className="mb-3 flex gap-2">
            {allowProposal ? <Button variant={preview === 'proposal' ? 'default' : 'outline'} onClick={() => setPreview('proposal')}>Proposal</Button> : null}
            {allowQuotation ? <Button variant={preview === 'quotation' ? 'default' : 'outline'} onClick={() => setPreview('quotation')}>Quotation</Button> : null}
            <Button variant="outline" onClick={() => {
              const doc = preview === 'proposal' ? proposal : quotation;
              if (doc) window.open(`/api/${preview === 'proposal' ? 'proposals' : 'quotations'}/${doc.id}/pdf`, '_blank');
            }}>Download</Button>
          </div>
          {preview === 'proposal' && proposal ? (
            <ProposalPreview number={proposal.number} title={proposal.title || ''} issuedOn={proposal.issuedOn || ''} customerName={proposal.customerName || ''} preparedFor={proposal.preparedFor || ''} preparedBy={proposal.preparedBy || ''} summary={proposal.summary || ''} purpose={proposal.purpose || ''} understanding={proposal.understanding || ''} approach={proposal.approach || ''} scope={proposal.scope || ''} stages={proposal.stages || ''} deliverables={proposal.deliverables || ''} commercial={proposal.commercial || ''} assumptions={proposal.assumptions || ''} whyMaxiron={proposal.whyMaxiron || ''} nextSteps={proposal.nextSteps || ''} />
          ) : null}
          {preview === 'quotation' && quotation ? (
            quotation.layout === '1'
              ? <QuotationPreview number={quotation.number} customerName={quotation.customerName || ''} title={quotation.title || ''} issuedOn={quotation.issuedOn || ''} currency={quotation.currency} validDays={quotation.validDays || 60} billToName={quotation.billToName || ''} billToAddress={quotation.billToAddress || ''} billToEmail={quotation.billToEmail || ''} contactName={quotation.contactName || ''} contactRole={quotation.contactRole || ''} contactEmail={quotation.contactEmail || ''} contactPhone={quotation.contactPhone || ''} notice={quotation.notice || ''} scope={quotation.scope || ''} paymentTerms={quotation.paymentTerms || ''} delivery={quotation.delivery || ''} acceptance={quotation.acceptance || ''} lines={lines} vatPence={quotation.vatPence ?? null} />
              : <QuotationFormat2 number={quotation.number} customerName={quotation.customerName || ''} title={quotation.title || ''} issuedOn={quotation.issuedOn || ''} currency={quotation.currency} validDays={quotation.validDays || 60} billToName={quotation.billToName || ''} billToAddress={quotation.billToAddress || ''} billToEmail={quotation.billToEmail || ''} contactName={quotation.contactName || ''} contactRole={quotation.contactRole || ''} contactEmail={quotation.contactEmail || ''} contactPhone={quotation.contactPhone || ''} notice={quotation.notice || ''} scope={quotation.scope || ''} paymentTerms={quotation.paymentTerms || ''} delivery={quotation.delivery || ''} acceptance={quotation.acceptance || ''} lines={lines} vatPence={quotation.vatPence ?? null} />
          ) : null}
        </div>
      </div>
      <p className="mt-4 text-sm text-[var(--bo-muted)]">Issuing creates a fixed revision and records the recipient and time.</p>
    </div>
  );
}
